// TranslationModule.js - Translation functionality module
import React from 'react';
import { Languages, Globe, Loader, ChevronDown, ChevronRight } from 'lucide-react';
import { LANGUAGES } from '../selectors/LanguageSelector';
import TranslationModelSelector from '../selectors/TranslationModelSelector';
import TranslationProgress from '../ItineraryCreator/translation/TranslationProgress';
import { DEFAULT_TRANSLATION_MODEL, estimateTranslationCost, TRANSLATION_PRICING } from '../constants/translationConfig';
import { useAccessControl } from '../../contexts/AccessControlContext';
import FeatureGuard from '../FeatureGuard';
import LinkedLanguageManager from './LinkedLanguageManager';

const TranslationModule = ({
  itinerary,
  targetLanguage = 'en-US',
  setTargetLanguage = () => {},
  translationModel = DEFAULT_TRANSLATION_MODEL,
  setTranslationModel = () => {},
  isTranslatingAll,
  translatingFields,
  translationProgress = [],
  onTranslateAll = () => {},
  onTranslateField = () => {},
  onTranslatePoiDetails = () => {},
  onTranslateAllPoiDetails = () => {},
  linkedLanguageProps = null,
  className = ''
}) => {
  const { getFieldAccess } = useAccessControl();

  const canTranslate = getFieldAccess('translation') === 'edit' || getFieldAccess('translation') === 'full';
  const [isTranslatingAllPois, setIsTranslatingAllPois] = React.useState(false);
  const [showIndividualPoiTranslation, setShowIndividualPoiTranslation] = React.useState(false);
  const estimate = React.useMemo(() => {
    const payload = JSON.stringify(itinerary || {});
    return estimateTranslationCost(payload, translationModel);
  }, [itinerary, translationModel]);

  const formatCurrency = (value) => `$${value.toFixed(4)}`;

  const handleTranslateAll = () => {
    if (canTranslate && onTranslateAll) {
      onTranslateAll();
    }
  };

  const handleTranslateField = (field) => {
    if (canTranslate && onTranslateField) {
      onTranslateField(field);
    }
  };

  const handleTranslateAllPoiDetails = () => {
    if (canTranslate && onTranslateAllPoiDetails) {
      setIsTranslatingAllPois(true);
      onTranslateAllPoiDetails();
    }
  };

  return (
    <FeatureGuard featureName="translation" requiredAccess="view_only">
      <div className={`space-y-6 ${className}`}>
        {linkedLanguageProps && (
          <LinkedLanguageManager
            currentGuide={itinerary}
            {...linkedLanguageProps}
          />
        )}

        <details className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <summary className="cursor-pointer list-none select-none px-6 py-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Languages className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">Translation</h2>
                  <p className="text-sm text-gray-600">Create new translated guide versions and translate stop details.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium max-w-[16rem] truncate">
                  {itinerary?.metadata?.name || 'Untitled'}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                  Language: {itinerary?.metadata?.language || 'en-us'}
                </span>
                <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                  {itinerary?.points?.length || 0} stops
                </span>
              </div>
            </div>
          </summary>

          <div className="px-6 pb-6">

        <div className="space-y-4 mb-6">
          {/* STEP 1: Setup */}
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">1</span>
              <h3 className="font-semibold text-gray-800">Choose language & model</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Language
                </label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!canTranslate}
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Translation Model
                </label>
                <TranslationModelSelector
                  value={translationModel}
                  onChange={setTranslationModel}
                  disabled={!canTranslate}
                />
              </div>
            </div>
            {estimate && TRANSLATION_PRICING[translationModel] && (
              <details className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
                <summary className="cursor-pointer px-3 py-2 flex items-center justify-between gap-3 select-none">
                  <span className="font-medium">Estimated translation cost</span>
                  <span className="font-semibold">{formatCurrency(estimate.total)}</span>
                </summary>
                <div className="px-3 pb-3">
                  <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-amber-800">
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-amber-700">Payload</div>
                      <div>{JSON.stringify(itinerary || {}).length.toLocaleString()} chars</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-amber-700">Model</div>
                      <div>{translationModel}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-amber-700">Input tokens</div>
                      <div>~{estimate.inputTokens.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-amber-700">Output tokens</div>
                      <div>~{estimate.outputTokens.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-amber-800">
                    Based on OpenAI pricing of ${TRANSLATION_PRICING[translationModel].inputPerMillion}/1M input tokens and ${TRANSLATION_PRICING[translationModel].outputPerMillion}/1M output tokens. Estimate only.
                  </div>
                </div>
              </details>
            )}
          </div>

          {/* STEP 2: Translate Basic Guide */}
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">2</span>
              <h3 className="font-semibold text-gray-800">Create translated guide version</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">Creates a new guide with a new ID and translates the basics: title, description, and stop names/descriptions.</p>
            <button
              onClick={handleTranslateAll}
              disabled={!canTranslate || isTranslatingAll}
              className={`w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors ${
                canTranslate && !isTranslatingAll
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isTranslatingAll ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Globe className="w-5 h-5" />
              )}
              {isTranslatingAll ? 'Creating Translated Version...' : 'Translate as New Guide Version'}
            </button>
          </div>

          {/* STEP 3: Translate POI Details */}
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">3</span>
              <h3 className="font-semibold text-gray-800">Translate stop details</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Translates the remaining content per stop: in-depth descriptions, historical context, cultural significance, hidden gems, subtitle, tips, etc.</p>

            {itinerary?.points && itinerary.points.length > 0 ? (
              <>
                {/* Batch Translation Button */}
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <button
                    onClick={handleTranslateAllPoiDetails}
                    disabled={!canTranslate || isTranslatingAllPois}
                    className={`w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors ${
                      canTranslate && !isTranslatingAllPois
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isTranslatingAllPois ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <Globe className="w-5 h-5" />
                    )}
                    {isTranslatingAllPois ? `Translating All ${itinerary.points.length} POI Details...` : `Translate All ${itinerary.points.length} POI Details`}
                  </button>
                  <p className="text-xs text-gray-600 mt-2">Translates all POI details in batch (faster than individual translations)</p>
                </div>

                {/* OR Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-xs text-gray-500 font-medium">OR</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                {/* Individual POI Translation Buttons */}
                <div className="rounded-lg border border-gray-200 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => setShowIndividualPoiTranslation((prev) => !prev)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">Translate Individual POIs</p>
                      <p className="text-xs text-gray-500">Expand only when you need per-stop translation control.</p>
                    </div>
                    {showIndividualPoiTranslation ? (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                  </button>

                  {showIndividualPoiTranslation && (
                    <div className="space-y-2 border-t border-gray-200 px-4 py-3">
                      {itinerary.points.map((poi, index) => (
                        <button
                          key={poi.id}
                          onClick={() => onTranslatePoiDetails(poi.id)}
                          disabled={!canTranslate || translatingFields[`poi-details-${poi.id}`]}
                          className={`w-full py-2 px-3 rounded-lg flex items-center justify-between text-sm font-medium transition-colors ${
                            canTranslate && !translatingFields[`poi-details-${poi.id}`]
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          <span>{index + 1}. {poi.name}</span>
                          {translatingFields[`poi-details-${poi.id}`] ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Globe className="w-4 h-4" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 italic">No POIs to translate</p>
            )}
          </div>
        </div>

        {/* Translation Progress */}
        {translationProgress.length > 0 && (
          <div className="mb-6">
            <h3 className="font-medium text-gray-800 mb-3">Translation Progress</h3>
            <TranslationProgress progress={translationProgress} />
          </div>
        )}

        {/* Instructions */}
        <details className="mt-6 rounded-lg bg-blue-50 border border-blue-200">
          <summary className="cursor-pointer px-4 py-3 font-medium text-blue-800 select-none">How translation works</summary>
          <ul className="px-4 pb-4 text-sm text-blue-700 space-y-1">
            <li>• Step 1: choose the target language and translation model</li>
            <li>• Step 2: "Translate as New Guide Version" creates the translated guide (basics only)</li>
            <li>• Step 3: translate the stop details — in batch, or per stop for precise control</li>
            <li>• Review translations before finalizing</li>
          </ul>
        </details>
          </div>
        </details>
      </div>
    </FeatureGuard>
  );
};

export default TranslationModule;
